import { useState } from 'react';
import {
  LayoutDashboard,
  FolderKanban,
  Activity,
  Settings,
  ClipboardList,
  Globe,
  Server,
  GitBranch,
  FileText,
  Database,
  Palette,
  Code,
  TestTube,
  HelpCircle,
} from 'lucide-react';
import { ROUTES } from '../../utils/constants';
import { useSandboxMode } from '../../../features/auth/hooks/useSandboxMode';

/**
 * Base navigation items visible to all users
 */
const BASE_NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: ROUTES.DASHBOARD },
  { id: 'workflow', label: 'Workflow', icon: GitBranch, path: ROUTES.WORKFLOW },
  { id: 'projects', label: 'Projects', icon: FolderKanban, path: ROUTES.INPUTS },
    { id: 'mock-servers', label: 'Mock Servers', shortLabel: 'Mocks', icon: Server, path: ROUTES.MOCK_SERVERS },
  { id: 'admin', label: 'Admin', icon: Settings, path: ROUTES.ADMIN },
  { id: 'environment', label: 'Environments', shortLabel: 'Environ', icon: Globe, path: ROUTES.ENVIRONMENT },
  { id: 'test-plans', label: 'Test Plans', shortLabel: 'Plans', icon: ClipboardList, path: ROUTES.SCHEDULED_TEST_PLANS },
  { id: 'test-logs', label: 'Test Logs', shortLabel: 'Logs', icon: Activity, path: ROUTES.TEST_LOGS },
  { id: 'help', label: 'Help', icon: HelpCircle, path: ROUTES.HELP },
];

/**
 * ImagoSidebar - the 88px icon rail. Postman lineage.
 *
 * A narrow vertical sidebar with icons stacked and labels below each icon.
 *
 * Active state = a 3x28px left nub + primary text + the primary glow. That
 * combination is the rail's own metaphor and appears nowhere else in the
 * product: content tabs get an underline, mode toggles get a filled pill, and
 * sidebar nav gets a tinted row. Never mix them.
 *
 * The glow reads --glow-primary rather than a literal rgba, which matters on
 * light theme where the token resolves to `none` - a blue halo does not
 * survive on white.
 * 
 * @param {Object} props
 * @param {string} [props.currentPath='/inputs'] - Current active path
 * @param {Object} [props.user] - User profile information
 * @param {string} [props.user.name] - User's display name
 * @param {string} [props.user.role] - User's role
 * @param {string} [props.user.avatar] - User's avatar URL
 * @param {Object} [props.organization] - Organization information
 * @param {string} [props.organization.name] - Organization name
 * @param {string} [props.organization.logo] - Organization logo URL
 * @param {Array} [props.recentItems] - Recently accessed items
 * @param {Function} [props.onNavigate] - Navigation callback
 * @param {string} [props.className] - Additional CSS classes
 */
export function ImagoSidebar({
  currentPath = '/inputs',
  user = { name: 'User', role: 'Developer' },
  organization = { name: 'IMAAQ' },
  recentItems = [],
  onNavigate,
  className = '',
  isOpen = false,
  onClose,
  activeRole = null,
  ...rest
}) {
  const [hoveredItem, setHoveredItem] = useState(null);
  const isSandbox = useSandboxMode();

  // IDs to grey out (disable) for sandbox users
  const SANDBOX_DISABLED_IDS = ['workflow', 'mock-servers', 'admin', 'environment'];

  const NAV_ITEMS = BASE_NAV_ITEMS;

  const handleNavClick = (path) => {
    if (onNavigate) {
      onNavigate(path);
    }
  };

  const isActive = (path) => {
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  return (
    <aside
      className={`
        fixed md:static inset-y-0 left-0 z-50
        transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        transition-transform duration-slow ease-standard
        flex flex-col items-center
        w-rail h-full py-2
        bg-background
        border-r border-border
        ${className}
      `.replace(/\s+/g, ' ').trim()}
      {...rest}
    >
      {/* Navigation Items - Vertical Icon Stack */}
      <nav className="flex-1 w-full overflow-y-auto overflow-x-hidden">
        <ul className="flex flex-col items-center gap-0.5 px-0.5">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const isHovered = hoveredItem === item.id;
            // Always the full label. shortLabel is kept on the items for
            // callers that genuinely need an abbreviation, but the rail is
            // wide enough for the longest one ("Environments", 68px at 11px),
            // and half-words like "Environ" read as a rendering bug.
            const displayLabel = item.label;
            const isDisabled = isSandbox && SANDBOX_DISABLED_IDS.includes(item.id);

            return (
              <li key={item.id} className="w-full">
                <button
                  onClick={() => !isDisabled && handleNavClick(item.path)}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  data-walkthrough={`sidebar-${item.id}`}
                  className={`
                    relative w-full flex flex-col items-center justify-center
                    py-2.5 px-1 rounded-lg
                    transition-[color,background-color,box-shadow] duration-base ease-standard
                    group
                    ${isDisabled
                      ? 'opacity-40 cursor-not-allowed'
                      : active
                        ? 'text-foreground shadow-glow-primary'
                        : isHovered
                          ? 'bg-card text-foreground'
                          : 'text-foreground-secondary hover:text-foreground'
                    }
                  `.replace(/\s+/g, ' ').trim()}
                  aria-current={active && !isDisabled ? 'page' : undefined}
                  title={isDisabled ? `${item.label} (Upgrade to access)` : item.label}
                  disabled={isDisabled}
                >
                  {/* Active indicator bar */}
                  {active && (
                    <span
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-7 rounded-r-full bg-primary"
                      aria-hidden="true"
                    />
                  )}

                  <Icon
                    className={`w-5 h-5 mb-1 transition-colors duration-base ease-standard ${active ? 'text-primary' : ''}`}
                    strokeWidth={2}
                  />
                  <span
                    /* The label stays foreground: primary at 11px is only
                       3.5:1 on the light ground. The icon and nub carry the
                       active signal, which is the rail's documented metaphor. */
                    className="text-label font-medium leading-tight text-center max-w-full truncate"
                  >
                    {displayLabel}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>


    </aside>
  );
}

export default ImagoSidebar;
